<template>
  <v-card class="ma-0">
    <v-toolbar color="primary" flat dark class="mb-4">
      <v-card-title>Edit Transaction</v-card-title>
    </v-toolbar>
    <v-card-text>
      <v-row class="mx-2">
        <v-col cols="12">
          <v-text-field v-model="receiptNo" label="Receipt No" hide-details="auto" dense outlined
                        :error-messages="verifyErrors.receipt_no" readonly />
        </v-col>

        <v-col cols="12">
          <v-autocomplete v-model="selectedStock" label="Stock Code" :items="computedStockList"
                          :search-input.sync="stockSearchInput" :loading="stockList.loading" item-value="stock_code"
                          return-object no-filter hide-details="auto" dense outlined :disabled="isLocked">
            <template #selection="{ item }">
              <span>{{ item.supplier_id }}-{{ item.stock_code }}</span>
              <span class="blue-grey--text mx-1 text-caption">{{ item.product_name }}</span>
            </template>
            <template #item="{ item }">
              <div class="d-flex justify-space-between w-100">
                <div class="d-flex">
                  <v-icon v-if="item.promotion">mdi-information-variant</v-icon>
                  <v-icon v-if="item.whole_sale?.conditions?.length">mdi-package</v-icon>
                  <span class="blue--text mx-1">{{ item.supplier_id }}-{{ item.stock_code }}</span>
                </div>
                <small>{{ item.product_name }}</small>
              </div>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col v-if="selectedStock?.whole_sale?.conditions?.length">
          <whole-sale-info :conditions="selectedStock.whole_sale.conditions" :quantity="quantity" :retail="retail" />
        </v-col>

        <v-col v-if="!!selectedStock?.promotion">
          <promo-info :promo-item="promoItem" :supplier-product="selectedStock" :allow-promo="allowPromo"
                      @update:allowPromo="allowPromo = $event" />
        </v-col>

        <v-col cols="12">
          <v-text-field v-model="refNumber" hide-details="auto" dense outlined label="Senior ID"
                        :error-messages="verifyErrors.ref_number" />
        </v-col>

        <v-col cols="12">
          <v-combobox v-model="consumer" hide-details="auto" dense outlined :items="computedConsumers"
                      :search-input.sync="searchConsumer" item-value="id" label="Consumer" no-filter return-object>
            <template #selection="{ item }">
              <small>({{ item.control_number }})</small>&nbsp;<span>{{ item.name }}</span>
            </template>
            <template #item="{ item }">
              <small>({{ item.control_number }})</small>&nbsp;<span>{{ item.name }}</span>
            </template>
          </v-combobox>
        </v-col>

        <v-col cols="12">
          <v-text-field v-model="dateTransaction" hide-details="auto" dense outlined type="date"
                        label="Transaction Date" :readonly="!authPermissions.has('sales:date_edit')"
                        :error-messages="verifyErrors.date_transaction" />
        </v-col>

        <v-col cols="12">
          <v-text-field v-model.number="retail" hide-details="auto" dense outlined label="SRP" prefix="₱" type="number"
                        :error-messages="verifyErrors.retail" />
        </v-col>

        <v-col cols="3">
          <v-text-field v-model.number="itemDiscountPercentage" hide-details="auto" dense outlined label="Discount/Item"
                        suffix="%" type="number" min="0" max="100"
                        :disabled="!!selectedStock?.promotion || !!selectedStock?.whole_sale?.conditions?.length" />
        </v-col>

        <v-col cols="9">
          <v-text-field v-model.number="discount" hide-details="auto" dense outlined label="Total Discounted Amnt"
                        prefix="₱" type="number" />
        </v-col>

        <v-col cols="12">
          <v-text-field v-model.number="quantity" hide-details="auto" dense outlined type="number"
                        :suffix="unit">
            <template #label>
              <span>Quantity</span>
              <span v-if="quantityLeft > 0" class="mx-1">({{ quantityLeft }} left)</span>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12">
          <v-textarea v-model="notes" hide-details="auto" dense outlined label="Notes" auto-grow no-resize rows="1" />
        </v-col>

        <v-col cols="12">
          <v-btn color="green" block :loading="saving" @click="updateTransaction">
            Update Transaction
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { moneyMixin } from '@/views/dashboard/mixins/helper.mixins'
import { mapState, mapActions } from 'vuex'
import { authAxios } from '@/extra-functions/axios.extra'
import { ErrorPopup, SuccessPopup, LoadingPopup, useWatchEffect, useDebounce } from '@/lib/utils'
import PromotionUtil from '@/lib/promotion.util'
import WholeSaleUtil from '@/lib/whole-sale.util'
import DiscountUtil from '@/lib/discount.util'

const [watchEffect, clearWatchEffect] = useWatchEffect()
const debounce = useDebounce()

export default {
  name: 'EditSaleInventoriesV2',
  components: {
    wholeSaleInfo: () => import('../WholeSaleInfo.vue'),
    promoInfo: () => import('../PromoInfo.vue'),
  },
  data() {
    return {
      ...inputVars(),
      ...consumerVars(),
      stockList: { list: [], loading: false },
      itemDiscountPercentage: null,
      isLocked: false,
      seniorDiscountRate: 0.2,
      storedStock: null,
      saleInventoryId: null,
      promoItem: null,
      saving: false,
      initialized: false,
    }
  },
  computed: {
    ...mapState({
      verifyErrors: state => state.saleInventory.verifyErrors,
      storeId: state => state.settings.storeId,
      authPermissions: state => state.auth.userPermissions,
    }),
    computedStockList() {
      if (!this.storedStock) return this.stockList.list
      const exists = this.stockList.list.some(s => s.stock_id === this.storedStock.stock_id)
      return exists ? this.stockList.list : [this.storedStock, ...this.stockList.list]
    },
    computedConsumers() {
      if (!this.storedConsumer) return this.consumers
      const exists = this.consumers.some(c => c.id === this.storedConsumer.id)
      return exists ? this.consumers : [this.storedConsumer, ...this.consumers]
    },
    quantityLeft() {
      return parseFloat(this.selectedStock?.q_remaining || 0)
    },
  },
  watch: {
    stockSearchInput() {
      this.stockList.loading = true
      debounce(this.fetchStocks)
    },
    searchConsumer() {
      this.fetchConsumers()
    },
    quantity() {
      if (!this.initialized) return
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    retail() {
      if (!this.initialized) return
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    refNumber() {
      if (!this.initialized) return
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    selectedStock(sp) {
      this.allowPromo = !!sp?.promotion
      if (!sp) {
        this.stockId = null
        this.supplierId = null
        if (this.initialized) {
          this.retail = null
          this.unit = null
          this.discount = 0
        }
        this.itemDiscountPercentage = null
        return
      }
      this.stockId = sp.stock_id
      this.supplierId = sp.supplier_id
      if (this.initialized) {
        this.retail = sp.retail
        this.unit = sp.unit
        if (!this.quantity) this.quantity = 1
        this.$nextTick(() => this.applyPromoAndDiscounts())
      }
    },
  },
  created() {
    this.fetchUnitsAll()
    this.fetchStocks()
    this.fetchConsumers()
    this.fetchSeniorDiscountRate()
  },
  beforeDestroy() {
    clearWatchEffect()
  },
  methods: {
    ...mapActions({
      fetchUnitsAll: 'unit/fetchAll',
    }),
    fetchStocks() {
      watchEffect(async (onCleanup) => {
        try {
          this.stockList.loading = true
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          const { data } = await authAxios().get('/v2/stock-suppliers/dropdown', {
            params: { search: this.stockSearchInput },
            signal: controller.signal,
          })
          this.stockList.list = data.data
        } catch (e) {
          ErrorPopup.fire('Error', 'Failed to get stocks')
        } finally {
          this.stockList.loading = false
        }
      }, 'SaleInventoryV2Edit_fetchStocks')
    },
    fetchConsumers() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          this.searchingConsumer = true
          const { data } = await authAxios().get('/v2/consumers/dropdown', {
            params: { search: this.searchConsumer },
            signal: controller.signal,
          })
          this.consumers = data?.data || []
        } catch (e) {
          console.error(e)
        } finally {
          this.searchingConsumer = false
        }
      }, 'SaleInventoryV2Edit_fetchConsumers')
    },
    fetchSeniorDiscountRate() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          const { data } = await authAxios().get('/v2/identity-card-types/senior-discount', { signal: controller.signal })
          this.seniorDiscountRate = data?.discountRate ?? 0.2
        } catch (e) {
          console.error(e)
        }
      }, 'SaleInventoryV2Edit_fetchSeniorDiscountRate')
    },
    init(saleInventoryId) {
      this.saleInventoryId = saleInventoryId
      watchEffect((onCleanUp) => {
        const controller = new AbortController()
        onCleanUp(() => controller.abort())
        this.fetchSaleData(saleInventoryId, controller)
      }, 'SaleInventoryV2Edit_init')
    },
    async fetchSaleData(saleInventoryId, controller) {
      try {
        LoadingPopup.fire('Loading', 'Fetching sale details...')
        const { data: sale } = await authAxios().get(`/v2/sale-inventories/${saleInventoryId}/edit`, {
          signal: controller?.signal,
        })
        if (!sale) throw new Error('sale not found')
        const stockSupplier = sale.stock_supplier
        this.initialized = false
        Object.assign(this, {
          receiptNo: sale.receipt_no,
          retail: sale.retail,
          quantity: sale.quantity,
          unit: sale.unit,
          discount: sale.discount,
          dateTransaction: sale.date_sold,
          notes: sale.notes,
          refNumber: sale.ref_number,
          consumer: sale.consumer,
          storedConsumer: sale.consumer,
          storedStock: stockSupplier,
          selectedStock: stockSupplier,
        })
        // Re-apply saved values after selectedStock watcher runs, then mark as initialized
        this.$nextTick(() => {
          this.retail = sale.retail
          this.unit = sale.unit
          this.discount = sale.discount
          this.initialized = true
        })
        LoadingPopup.close()
      } catch (e) {
        console.error(e)
        LoadingPopup.close()
        ErrorPopup.fire('Error', 'Failed to fetch sale data.')
      }
    },
    async updateTransaction() {
      try {
        this.saving = true
        LoadingPopup.fire('Updating', 'Saving changes...')
        await authAxios().put(`/v2/sale-inventories/${this.saleInventoryId}`, {
          receipt_no: this.receiptNo,
          stock_id: this.stockId,
          supplier_id: this.supplierId,
          unit: this.unit,
          quantity: this.quantity,
          retail: this.retail,
          discount: this.discount,
          notes: this.notes,
          date_sold: this.dateTransaction,
          ref_number: this.refNumber,
          consumer_id: this.consumer?.id ?? null,
        })
        SuccessPopup.fire('Success', 'Sale successfully updated')
        this.$emit('update-success')
        this.$emit('close-dialog')
      } catch (e) {
        console.error(e)
        LoadingPopup.close()
        ErrorPopup.fire('Error', 'Failed to update sale.')
      } finally {
        this.saving = false
      }
    },
    applyPromoAndDiscounts() {
      if (!this.stockId) return
      const promoUtil = new PromotionUtil(this.stockId, this.selectedStock?.promotion?.promoItem)
      promoUtil.setQuantity(this.quantity)
      this.promoItem = promoUtil.getPromoItem()

      const wholeSaleUtil = new WholeSaleUtil(this.selectedStock?.whole_sale?.conditions || [])
      wholeSaleUtil.setQuantity(this.quantity)
      wholeSaleUtil.setRetail(this.retail)

      const seniorDiscountRate = Number(this.refNumber ? this.seniorDiscountRate : 0)
      const discountUtil = new DiscountUtil(promoUtil.getDiscount(), wholeSaleUtil.getDiscount(), seniorDiscountRate)
      discountUtil.setQuantity(this.quantity)
      discountUtil.setRetail(this.retail)
      this.itemDiscountPercentage = Math.round(discountUtil.getDiscountRate() * 1000) / 10
      this.discount = Math.round(discountUtil.getDiscount() * 100) / 100
    },
  },
  mixins: [moneyMixin],
}

function inputVars() {
  return {
    receiptNo: null,
    selectedStock: null,
    stockSearchInput: null,
    stockId: null,
    supplierId: null,
    retail: null,
    quantity: null,
    discount: null,
    dateTransaction: null,
    notes: null,
    refNumber: null,
    allowPromo: false,
    unit: '',
  }
}

function consumerVars() {
  return {
    consumer: null,
    consumers: [],
    searchConsumer: null,
    searchingConsumer: false,
    storedConsumer: null,
  }
}
</script>
